import React, { useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";
import messages from "./messages";
import { useTheme, Box, Paper, CircularProgress, IconButton, Chip, Tooltip, Stack } from "@mui/material";
import { tokens } from "../../../assets/theme";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import DeleteIcon from "@mui/icons-material/Delete";
import { DataGridPro, useGridApiRef } from "@mui/x-data-grid-pro";
import { svSE, enUS } from "@mui/x-data-grid/locales";

const roleNameMap = (roles) => {
    const map = new Map();
    (roles || []).forEach(r => map.set(r.id, r.roleName));
    return map;
};

export default function UsersDataGrid(props) {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const apiRef = useGridApiRef();
    const { formatMessage } = useIntl();
    const language = localStorage.getItem("language") || "en";
    const translations = language === "sv" ? svSE : enUS;

    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const columnOrderLocalStorageName = "users-columnOrder";
    const columnWidthsLocalStorageName = "users-columnWidths";

    const reorderColumns = (orderArray, columnsToFix) => {
        const reordered = orderArray
            .map((field) => columnsToFix.find((col) => col.field === field))
            .filter(Boolean);
        const remaining = columnsToFix.filter((col) => !reordered.includes(col));
        return [...reordered, ...remaining];
    };

    const applyColumnWidths = (widthMap, columnsToFix) =>
        columnsToFix.map((col) => ({
            ...col,
            width: widthMap[col.field] || col.width,
            flex: widthMap[col.field] !== undefined ? undefined : col.flex,
        }));

    const initColumnsFromLocalStorage = (cols) => {
        let out = cols;
        const savedOrder = localStorage.getItem(columnOrderLocalStorageName);
        if (savedOrder) out = reorderColumns(JSON.parse(savedOrder), out);
        const savedWidths = localStorage.getItem(columnWidthsLocalStorageName);
        if (savedWidths) out = applyColumnWidths(JSON.parse(savedWidths), out);
        return out;
    };
    const roleNameById = React.useMemo(() => roleNameMap(props.roles), [props.roles]);

    const baseColumns = useMemo(() => [
        { field: "username", headerName: formatMessage(messages.usernameColumnLabel), flex: 1 },
        { field: "name", headerName: formatMessage(messages.nameColumnLabel), flex: 1 },
        {
            field: "roles",
            headerName: formatMessage(messages.rolesColumnLabel),
            flex: 2,
            valueGetter: (params) => {
                const ids = (params.row && params.row.roles) || [];
                return ids
                    .slice()
                    .sort((a, b) => a - b)
                    .map(id => roleNameById.get(id) || `#${id}`)
                    .join(", ");
            },
            renderCell: (params) => {
                const roleColors = {
                    1: { bg: colors.redAccent[600], text: "#fff" },   
                    2: { bg: "#1565C0", text: "#fff" },   
                    3: { bg: colors.dashbordGreen[600], text: "#fff" },   
                };

                const roleIds = (params.row && params.row.roles) || [];
                const sorted = roleIds.slice().sort((a, b) => a - b);

                return (
                    <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} sx={{ width: "100%" }}>
                        {sorted.map((id) => {
                            const label = roleNameById.get(id) || `#${id}`;
                            const color = roleColors[id] || { bg: "#e0e0e0", text: "#000" };
                            const chip = (
                                <Chip 
                                key={id} 
                                label={label} 
                                size="small" 
                                variant="outlined"
                                sx={{
                                    backgroundColor: color.bg,
                                    color: color.text,
                                    fontWeight: 500,
                                }}
                                />
                            );
                            return roleNameById.has(id)
                                ? chip
                                : <Tooltip key={id} title={formatMessage(messages.unknownRoleNameTooltip, { id })}>{chip}</Tooltip>;
                        })}
                    </Stack>
                );
            },
            sortable: false,
        },
        {
            field: "edit",
            headerName: "",
            renderCell: (params) => (
                <>
                    <IconButton
                        aria-label={formatMessage(messages.editUserLabel)}
                        size="small"
                        sx={{ "&:hover": { backgroundColor: colors.primary[500] }, backgroundColor: colors.primary[700] }}
                        onClick={() => props.handleEditUserClick(params.row.id)}
                    >
                        <RemoveRedEyeOutlinedIcon fontSize="inherit" />
                    </IconButton>
                    <IconButton
                        aria-label={formatMessage(messages.deleteUserLabel)}
                        size="small"
                        sx={{ "&:hover": { backgroundColor: colors.primary[500] }, backgroundColor: colors.primary[700], marginLeft: "10px" }}
                        onClick={() => props.handleDeleteUserClick(params.row.id)}
                    >
                        <DeleteIcon fontSize="inherit" />
                    </IconButton>
                </>
            ),
            width: 90,
        },
    ], [formatMessage, messages, colors, roleNameById, props.handleEditUserClick, props.handleDeleteUserClick]);

    const columns = useMemo(() => initColumnsFromLocalStorage(baseColumns), [baseColumns]);

    const handleColumnOrderChange = () => {
        if (!apiRef.current) return;
        const fields = apiRef.current.getVisibleColumns().map((c) => c.field);
        localStorage.setItem(columnOrderLocalStorageName, JSON.stringify(fields));
    };

    const handleColumnResize = (params) => {
        const { colDef, width } = params;
        const map = JSON.parse(localStorage.getItem(columnWidthsLocalStorageName)) || {};
        map[colDef.field] = width;
        localStorage.setItem(columnWidthsLocalStorageName, JSON.stringify(map));
    };

    if (props.users.isLoading) {
        return (
            <Box padding={2}>
                <Paper sx={{ position: "relative", borderRadius: "4px" }}>
                    <CircularProgress color="secondary" />
                </Paper>
            </Box>
        );
    }

    return (
        <Box padding={2} paddingTop={8}>
            <Paper sx={{ position: "relative", borderRadius: "4px" }}>
                <Box
                    m="0px 0 0 0"
                    height="76vh"
                    sx={{
                        "& .MuiDataGrid-root": {
                            backgroundColor: colors.primary[400],
                            borderColor: `${colors.primary[700]} !important`,
                            borderRadius: "0px",
                            fontSize: "1.0rem",
                            border: "0 !important",
                        },
                        "& .MuiDataGrid-main": {
                            borderRadius: "0px",
                            border: "0 !important",
                            borderColor: `${colors.primary[700]} !important`,
                        },
                        "& .MuiDataGrid-cell": {
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px !important",
                        },
                        "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: colors.primary[400],
                            fontSize: "1.0rem",
                            borderColor: `${colors.primary[700]} !important`,
                            borderWidth: "2px",
                        },
                        "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
                        "& .MuiDataGrid-footerContainer": {
                            borderTop: "none",
                            backgroundColor: colors.primary[600],
                        },
                        "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
                            color: `${colors.gray[100]} !important`,
                        },
                        "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIconButton": {
                            opacity: 0,
                            transition: "opacity 0.2s",
                        },
                        "& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIconButton": { opacity: 1 },
                    }}
                >
                    <DataGridPro
                        apiRef={apiRef}
                        localeText={translations.components.MuiDataGrid.defaultProps.localeText}
                        onColumnResize={handleColumnResize}
                        onColumnOrderChange={handleColumnOrderChange}
                        rows={props.users.data || []}     
                        getRowId={(row) => row.id}
                        columns={columns}
                        columnVisibilityModel={columnVisibilityModel}
                        onColumnVisibilityModelChange={(m) => setColumnVisibilityModel(m)}
                        slotProps={{
                            panel: {
                                sx: {
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#799c4a", opacity: 0.3 },
                                    "& .MuiSwitch-switchBase.Mui-checked .MuiSwitch-thumb": { backgroundColor: "#799c4a" },
                                },
                            },
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
